import React, { useMemo, useState } from 'react';
import type { RolPermiso } from '../types';
import { PageShell, glassLight, primaryButton, ghostButton, dangerButton, EmptyState, MotionGrid, MotionItem, useIsMobile, usePagination, Pagination, T, Modal } from '../ui';
// Módulos actuales del sistema (orden de despliegue). Cualquier permiso viejo
// guardado para módulos que ya no existen (mapa, equipos, empleados, etc.) no
// se muestra.
const MODULOS_ACTUALES = ['consumo', 'costoConsumo', 'sucursales', 'reportes', 'alertas', 'usuarios', 'roles', 'auditoria', 'configuracion'] as const;
const moduleLabels: Record<string, string> = {
  consumo: 'Consumo',
  costoConsumo: 'Ver costo estimado',
  sucursales: 'Sucursales',
  reportes: 'Reportes',
  alertas: 'Alertas',
  usuarios: 'Usuarios',
  roles: 'Roles',
  auditoria: 'Auditoría',
  configuracion: 'Configuración'
};
const sinPermiso = {
  leer: false,
  editar: false,
  archivar: false
};
export function RolesPermisos({
  roles,
  onNew,
  onEdit,
  onDelete,
  canArchive
}: {
  roles: RolPermiso[];
  onNew: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  canArchive: boolean;
}) {
  const [tab, setTab] = useState<'activos' | 'archivados'>('activos');
  const [confirm, setConfirm] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const filteredRoles = useMemo(() => {
    return roles.filter(r => tab === 'activos' ? !r.archivado : r.archivado);
  }, [roles, tab]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(filteredRoles, 6);
  return <PageShell title="Roles y permisos" sub="Control de accesos por perfil del sistema" action={<button onClick={onNew} style={primaryButton} data-id="element-533">
          + Nuevo rol
        </button>} data-id="element-532">
      <div style={{
      display: 'flex',
      gap: 8,
      marginBottom: 20
    }} data-id="element-534">
        <button onClick={() => {
        setTab('activos');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'activos' ? T.accentSoft : 'transparent',
        color: tab === 'activos' ? T.accent : T.textMuted
      }} data-id="element-535">
          Activos
        </button>
        {canArchive && <button onClick={() => {
        setTab('archivados');
        setPage(1);
      }} style={{
        ...ghostButton,
        background: tab === 'archivados' ? T.accentSoft : 'transparent',
        color: tab === 'archivados' ? T.accent : T.textMuted
      }} data-id="element-536">
            Archivados
          </button>}
      </div>

      {filteredRoles.length === 0 ? <EmptyState title="Sin roles definidos" hint="Crea perfiles para controlar qué puede ver y editar cada usuario." icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" data-id="element-538">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-539" />
            </svg>} data-id="element-537" /> : <MotionGrid style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))',
      gap: 14
    }} data-id="element-540">
          {pageItems.map(rol => <MotionItem key={rol.id} className="getr-card" style={{
        ...glassLight,
        padding: 20
      }} data-id="element-541">
              <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          gap: 12
        }} data-id="element-542">
                <div style={{
            display: 'flex',
            gap: 12,
            minWidth: 0
          }} data-id="element-543">
                  <div style={{
              width: 38,
              height: 38,
              borderRadius: 11,
              flexShrink: 0,
              background: T.accentSoft,
              border: `1px solid ${T.accentBorder}`,
              color: T.accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }} data-id="element-544">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-545">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-546" />
                    </svg>
                  </div>
                  <div style={{
              minWidth: 0
            }} data-id="element-547">
                    <div style={{
                fontSize: 16,
                fontWeight: 700,
                color: T.text
              }} data-id="element-548">
                      {rol.nombre}
                    </div>
                    <div style={{
                marginTop: 5,
                color: T.textSecondary,
                fontSize: 13
              }} data-id="element-549">
                      {rol.descripcion}
                    </div>
                  </div>
                </div>
                <div style={{
            display: 'flex',
            gap: 8,
            flexShrink: 0,
            width: isMobile ? '100%' : 'auto'
          }} data-id="element-550">
                  <button className="getr-btn-ghost" onClick={() => onEdit(rol.id)} style={{
              ...ghostButton,
              flex: isMobile ? 1 : 'none'
            }} data-id="element-551">
                    Editar
                  </button>
                  <button onClick={() => setConfirm(rol.id)} style={{
              ...ghostButton,
              flex: isMobile ? 1 : 'none',
              color: rol.archivado ? T.info : T.warn,
              borderColor: rol.archivado ? 'rgba(96,165,250,0.3)' : 'rgba(251,191,36,0.3)',
              background: rol.archivado ? 'rgba(96,165,250,0.1)' : 'rgba(251,191,36,0.1)'
            }} data-id="element-552">
                    {rol.archivado ? 'Desarchivar' : 'Archivar'}
                  </button>
                </div>
              </div>
              <div style={{
          marginTop: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }} data-id="element-553">
                {MODULOS_ACTUALES.map(modulo => {
            const acciones = rol.permisos[modulo] ?? sinPermiso;
            return <div key={modulo} style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'minmax(0,1fr) repeat(3,46px)' : '1fr repeat(3,64px)',
              alignItems: 'center',
              gap: isMobile ? 5 : 8,
              padding: '8px 0',
              borderTop: `1px solid ${T.border}`
            }} data-id="element-554">
                      <span style={{
                color: T.textSecondary,
                fontSize: 13,
                fontWeight: 600
              }} data-id="element-555">
                        {moduleLabels[modulo] || modulo}
                      </span>
                      {(['leer', 'editar', 'archivar'] as const).map(accion => <span key={accion} title={accion} style={{
                textAlign: 'center',
                padding: isMobile ? '5px 3px' : '5px 7px',
                borderRadius: T.rSm,
                background: acciones[accion] ? T.accentSoft : 'rgba(255,255,255,.04)',
                border: acciones[accion] ? `1px solid ${T.accentBorder}` : `1px solid ${T.border}`,
                color: acciones[accion] ? T.accent : T.textMuted,
                fontSize: isMobile ? 9.5 : 11,
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }} data-id="element-556">
                            {isMobile ? {
                  leer: 'Ver',
                  editar: 'Edit',
                  archivar: 'Arch'
                }[accion] : accion}
                          </span>)}
                    </div>;
          })}
              </div>
            </MotionItem>)}
        </MotionGrid>}
      {roles.length > 0 && <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="roles" data-id="element-557" />}

      {confirm && <Modal title="Confirmar acción" onClose={() => setConfirm(null)} data-id="element-558">
          <div style={{
        color: T.textSecondary,
        marginBottom: 24,
        fontSize: 14,
        lineHeight: 1.5
      }} data-id="element-559">
            ¿Estás seguro de que deseas{' '}
            {roles.find(r => r.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}{' '}
            este rol?
          </div>
          <div style={{
        display: 'flex',
        gap: 12,
        justifyContent: 'flex-end'
      }} data-id="element-560">
            <button onClick={() => setConfirm(null)} style={{
          ...ghostButton,
          padding: '10px 20px'
        }} data-id="element-561">
              Cancelar
            </button>
            <button onClick={() => {
          onDelete(confirm);
          setConfirm(null);
        }} style={{
          ...primaryButton,
          background: roles.find(r => r.id === confirm)?.archivado ? T.info : T.warn,
          borderColor: roles.find(r => r.id === confirm)?.archivado ? T.info : T.warn,
          color: '#0B1120'
        }} data-id="element-562">
              Sí,{' '}
              {roles.find(r => r.id === confirm)?.archivado ? 'desarchivar' : 'archivar'}
            </button>
          </div>
        </Modal>}
    </PageShell>;
}